<template>
  <section class="trusted-by">
    <h2><strong>{{ $t('partners.title') }}</strong></h2>
    <p class="section-subtitle">{{ $t('partners.subtitle') }}</p>

    <!-- Desktop Grid -->
    <div class="partners-grid desktop-only">
      <PartnerCard
        v-for="partner in partners"
        :key="partner.id"
        :partner="partner"
        v-show="!partner.isCallToAction"
        @open-embed="openInstagramEmbed(partner)"
      />
    </div>

    <!-- Mobile Slider -->
    <div class="mobile-slider mobile-only">
      <div class="slider-container" ref="sliderContainer">
        <div 
          class="slider-track" 
          ref="sliderTrack"
          :style="{ transform: `translateX(-${currentSlide * slideWidth}px)` }"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <div 
            v-for="partner in partners" 
            :key="partner.id"
            class="slider-slide"
            ref="slides"
          >
            <PartnerCard :partner="partner" @open-embed="openInstagramEmbed(partner)" />
          </div>
        </div>
      </div>
      
      <div class="slider-nav">
        <button 
          class="nav-btn prev-btn" 
          @click="prevSlide"
          :disabled="currentSlide === 0"
        >
          ‹
        </button>
        
        <div class="slider-dots">
          <button
            v-for="(partner, index) in partners"
            :key="partner.id"
            class="dot"
            :class="{ active: index === currentSlide }"
            @click="goToSlide(index)"
          ></button>
        </div>
        
        <button 
          class="nav-btn next-btn" 
          @click="nextSlide"
          :disabled="currentSlide === partners.length - 1"
        >
          ›
        </button>
      </div>
      
      <div class="slide-counter">
        {{ currentSlide + 1 }} / {{ partners.length }}
      </div>
    </div>
  </section>
</template>

<script>
import PartnerCard from './PartnerCard.vue'

const PARTNER_IDS = ['antonia', 'alexandra', 'karolina', 'tanja', 'robert', 'lewis', 'cta']

const PARTNER_IMAGES = {
  antonia: 'https://yt3.googleusercontent.com/ESHN8gFm0sJQhm0lkV0isiW30QQ2cqd8xqFmWkHZiU7AlPhq_rSoqO7Eup1BqvP3uGWSzIX-kQ=s900-c-k-c0x00ffffff-no-rj',
  alexandra: 'https://mal-ehrlich.ch/wp-content/uploads/2024/01/Alexandra-Kruse-1.jpg',
  karolina: 'https://sklep-jakowniebie.pl/wp-content/uploads/elementor/thumbs/Karola_3-1-qvejfl24xs28tngabr06yerpv2tdp38djgijcbzd6w.png',
  tanja: 'https://m.media-amazon.com/images/S/amzn-author-media-prod/i27cgpdidr6t5srp046bfpsi0d.jpg',
  robert: 'https://astrodaddy.de/wp-content/uploads/2024/10/Rob-9.png',
  lewis: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR-fe9VfF8L2Kab0NSvvfqywSpPXN7IjCtMK5HWsYxPtpCLWQJtym79cQ7mBLU9A9qY1Gc&usqp=CAU',
  cta: '?'
}

export default {
  name: 'PartnersSection',
  components: {
    PartnerCard
  },
  data() {
    return {
      currentSlide: 0,
      slideWidth: 0,
      touchStartX: 0,
      touchEndX: 0,
      isDragging: false
    }
  },
  computed: {
    partners() {
      return PARTNER_IDS.map((id) => {
        const achievements = this.$tm(`partners.items.${id}.achievements`)
        return {
          id,
          name: this.$t(`partners.items.${id}.name`),
          title: this.$t(`partners.items.${id}.title`),
          image: PARTNER_IMAGES[id],
          followers: this.$t(`partners.items.${id}.followers`),
          achievements: Array.isArray(achievements) ? achievements : [],
          isCallToAction: id === 'cta',
          hasTestimonial: id === 'robert'
        }
      })
    }
  },
  mounted() {
    this.calculateSlideWidth()
    window.addEventListener('resize', this.calculateSlideWidth)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.calculateSlideWidth)
  },
  methods: {
    openInstagramEmbed(partner) {
      if (!partner?.hasTestimonial) return
      const container = document.createElement('div')
      container.className = 'ig-embed-modal'
      container.innerHTML = [
        '<div class="ig-embed-backdrop"></div>',
        '<div class="ig-embed-content">',
        '  <button class="ig-embed-close">×</button>',
        '  <blockquote class="instagram-media" data-instgrm-captioned data-instgrm-permalink="https://www.instagram.com/reel/DLuUDQKtVqO/?utm_source=ig_embed&amp;utm_campaign=loading" data-instgrm-version="14" style=" background:#FFF; border:0; border-radius:3px; box-shadow:0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15); margin: 1px; max-width:540px; min-width:326px; padding:0; width:99.375%; width:-webkit-calc(100% - 2px); width:calc(100% - 2px);"><div style="padding:16px;"> <a href="https://www.instagram.com/reel/DLuUDQKtVqO/?utm_source=ig_embed&amp;utm_campaign=loading" style=" background:#FFFFFF; line-height:0; padding:0 0; text-align:center; text-decoration:none; width:100%;" target="_blank"> <div style=" display: flex; flex-direction: row; align-items: center;"> <div style="background-color: #F4F4F4; border-radius: 50%; flex-grow: 0; height: 40px; margin-right: 14px; width: 40px;"></div> <div style="display: flex; flex-direction: column; flex-grow: 1; justify-content: center;"> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; margin-bottom: 6px; width: 100px;"></div> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; width: 60px;"></div></div></div><div style="padding: 19% 0;"></div> <div style="display:block; height:50px; margin:0 auto 12px; width:50px;"><svg width="50px" height="50px" viewBox="0 0 60 60" version="1.1" xmlns="https://www.w3.org/2000/svg" xmlns:xlink="https://www.w3.org/1999/xlink"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-511.000000, -20.000000)" fill="#000000"><g><path d="M556.869,30.41 C554.814,30.41 553.148,32.076 553.148,34.131 C553.148,36.186 554.814,37.852 556.869,37.852 C558.924,37.852 560.59,36.186 560.59,34.131 C560.59,32.076 558.924,30.41 556.869,30.41 M541,60.657 C535.114,60.657 530.342,55.887 530.342,50 C530.342,44.114 535.114,39.342 541,39.342 C546.887,39.342 551.658,44.114 551.658,50 C551.658,55.887 546.887,60.657 541,60.657 M541,33.886 C532.1,33.886 524.886,41.1 524.886,50 C524.886,58.899 532.1,66.113 541,66.113 C549.9,66.113 557.115,58.899 557.115,50 C557.115,41.1 549.9,33.886 541,33.886 M565.378,62.101 C565.244,65.022 564.756,66.606 564.346,67.663 C563.803,69.06 563.154,70.057 562.106,71.106 C561.058,72.155 560.06,72.803 558.662,73.347 C557.607,73.757 556.021,74.244 553.102,74.378 C549.944,74.521 548.997,74.552 541,74.552 C533.003,74.552 532.056,74.521 528.898,74.378 C525.979,74.244 524.393,73.757 523.338,73.347 C521.94,72.803 520.942,72.155 519.894,71.106 C518.846,70.057 518.197,69.06 517.654,67.663 C517.244,66.606 516.755,65.022 516.623,62.101 C516.479,58.943 516.448,57.996 516.448,50 C516.448,42.003 516.479,41.056 516.623,37.899 C516.755,34.978 517.244,33.391 517.654,32.338 C518.197,30.938 518.846,29.942 519.894,28.894 C520.942,27.846 521.94,27.196 523.338,26.654 C524.393,26.244 525.979,25.756 528.898,25.623 C532.057,25.479 533.004,25.448 541,25.448 C548.997,25.448 549.943,25.479 553.102,25.623 C556.021,25.756 557.607,26.244 558.662,26.654 C560.06,27.196 561.058,27.846 562.106,28.894 C563.154,29.942 563.803,30.938 564.346,32.338 C564.756,33.391 565.244,34.978 565.378,37.899 C565.522,41.056 565.552,42.003 565.552,50 C565.552,57.996 565.522,58.943 565.378,62.101 M570.82,37.631 C570.674,34.438 570.167,32.258 569.425,30.349 C568.659,28.377 567.633,26.702 565.965,25.035 C564.297,23.368 562.623,22.342 560.652,21.575 C558.743,20.834 556.562,20.326 553.369,20.18 C550.169,20.033 549.148,20 541,20 C532.853,20 531.831,20.033 528.631,20.18 C525.438,20.326 523.257,20.834 521.349,21.575 C519.376,22.342 517.703,23.368 516.035,25.035 C514.368,26.702 513.342,28.377 512.574,30.349 C511.834,32.258 511.326,34.438 511.181,37.631 C511.035,40.831 511,41.851 511,50 C511,58.147 511.035,59.17 511.181,62.369 C511.326,65.562 511.834,67.743 512.574,69.651 C513.342,71.625 514.368,73.296 516.035,74.965 C517.703,76.634 519.376,77.658 521.349,78.425 C523.257,79.167 525.438,79.673 528.631,79.82 C531.831,79.965 532.853,80.001 541,80.001 C549.148,80.001 550.169,79.965 553.369,79.82 C556.562,79.673 558.743,79.167 560.652,78.425 C562.623,77.658 564.297,76.634 565.965,74.965 C567.633,73.296 568.659,71.625 569.425,69.651 C570.167,67.743 570.674,65.562 570.82,62.369 C570.966,59.17 571,58.147 571,50 C571,41.851 570.966,40.831 570.82,37.631"></path></g></g></g></svg></div><div style="padding-top: 8px;"> <div style=" color:#3897f0; font-family:Arial,sans-serif; font-size:14px; font-style:normal; font-weight:550; line-height:18px;">Sieh dir diesen Beitrag auf Instagram an</div></div><div style="padding: 12.5% 0;"></div> <div style="display: flex; flex-direction: row; margin-bottom: 14px; align-items: center;"><div> <div style="background-color: #F4F4F4; border-radius: 50%; height: 12.5px; width: 12.5px; transform: translateX(0px) translateY(7px);"></div> <div style="background-color: #F4F4F4; height: 12.5px; transform: rotate(-45deg) translateX(3px) translateY(1px); width: 12.5px; flex-grow: 0; margin-right: 14px; margin-left: 2px;"></div> <div style="background-color: #F4F4F4; border-radius: 50%; height: 12.5px; width: 12.5px; transform: translateX(9px) translateY(-18px);"></div></div><div style="margin-left: 8px;"> <div style=" background-color: #F4F4F4; border-radius: 50%; flex-grow: 0; height: 20px; width: 20px;"></div> <div style=" width: 0; height: 0; border-top: 2px solid transparent; border-left: 6px solid #f4f4f4; border-bottom: 2px solid transparent; transform: translateX(16px) translateY(-4px) rotate(30deg)"></div></div><div style="margin-left: auto;"> <div style=" width: 0px; border-top: 8px solid #F4F4F4; border-right: 8px solid transparent; transform: translateY(16px);"></div> <div style=" background-color: #F4F4F4; flex-grow: 0; height: 12px; width: 16px; transform: translateY(-4px);"></div> <div style=" width: 0; height: 0; border-top: 8px solid #F4F4F4; border-left: 8px solid transparent; transform: translateY(-4px) translateX(8px);"></div></div></div> <div style="display: flex; flex-direction: column; flex-grow: 1; justify-content: center; margin-bottom: 24px;"> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; margin-bottom: 6px; width: 224px;"></div> <div style=" background-color: #F4F4F4; border-radius: 4px; flex-grow: 0; height: 14px; width: 144px;"></div></div></a><p style=" color:#c9c8cd; font-family:Arial,sans-serif; font-size:14px; line-height:17px; margin-bottom:0; margin-top:8px; overflow:hidden; padding:8px 0 7px; text-align:center; text-overflow:ellipsis; white-space:nowrap;"><a href="https://www.instagram.com/reel/DLuUDQKtVqO/?utm_source=ig_embed&amp;utm_campaign=loading" style=" color:#c9c8cd; font-family:Arial,sans-serif; font-size:14px; font-style:normal; font-weight:normal; line-height:17px; text-decoration:none;" target="_blank">Ein Beitrag geteilt von Dawid mit W | Chatbot Marketing für Astrologie &amp; Meditation (@personalcoding.de)</a></p></div></blockquote>',
        '</div>'
      ].join('')
      document.body.appendChild(container)
      const close = () => container.remove()
      container.querySelector('.ig-embed-backdrop').addEventListener('click', close)
      container.querySelector('.ig-embed-close').addEventListener('click', close)
      
      if (!window.instgrm) {
        const script = document.createElement('script')
        script.async = true
        script.src = '//www.instagram.com/embed.js'
        document.head.appendChild(script)
      }
      
      if (window.instgrm && window.instgrm.Embeds) {
        window.instgrm.Embeds.process()
      }
    },
    calculateSlideWidth() {
      if (this.$refs.sliderContainer) {
        this.slideWidth = this.$refs.sliderContainer.offsetWidth
      }
    },
    nextSlide() {
      if (this.currentSlide < this.partners.length - 1) {
        this.currentSlide++
      }
    },
    prevSlide() {
      if (this.currentSlide > 0) {
        this.currentSlide--
      }
    },
    goToSlide(index) {
      this.currentSlide = index
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX
      this.touchEndX = e.touches[0].clientX
      this.isDragging = true
    },
    handleTouchMove(e) {
      if (!this.isDragging) return
      e.preventDefault()
      this.touchEndX = e.touches[0].clientX
      
      const touchDiff = this.touchStartX - this.touchEndX
      const maxMove = 50
      const moveAmount = Math.max(-maxMove, Math.min(maxMove, touchDiff * 0.3))
      
      if (this.$refs.sliderTrack) {
        this.$refs.sliderTrack.style.transform = `translateX(calc(-${this.currentSlide * this.slideWidth}px + ${moveAmount}px))`
      }
    },
    handleTouchEnd() {
      if (!this.isDragging) return
      this.isDragging = false
      
      const touchDiff = this.touchStartX - this.touchEndX
      const minSwipeDistance = 50
      const swipeThreshold = 100
      
      if (this.$refs.sliderTrack) {
        this.$refs.sliderTrack.style.transform = `translateX(-${this.currentSlide * this.slideWidth}px)`
      }
      
      if (Math.abs(touchDiff) > minSwipeDistance) {
        if (touchDiff > 0) {
          this.nextSlide()
        } else {
          this.prevSlide()
        }
      } else if (Math.abs(touchDiff) > swipeThreshold) {
        if (touchDiff > 0) {
          this.nextSlide()
        } else {
          this.prevSlide()
        }
      }
      
      this.touchStartX = 0
      this.touchEndX = 0
    }
  }
}
</script>

<style scoped>
/* Partners Section Theme Support */
.trusted-by {
  padding: 80px 20px;
  background: var(--gradient-secondary);
  text-align: center;
}

.trusted-by h2 {
  font-size: 2.5rem;
  margin-bottom: 15px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  color: var(--text-secondary);
}

.section-subtitle {
  text-align: center;
  color: var(--text-secondary);
  margin-bottom: 50px;
  font-size: 1.2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.partners-grid {
  display: grid !important;
  grid-template-columns: repeat(3, 1fr) !important;
  grid-template-rows: repeat(2, 1fr) !important;
  gap: 40px;
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 20px;
}

.mobile-slider {
  display: none;
  max-width: 100%;
  margin: 40px auto 0;
}

.slider-container {
  overflow: hidden;
  width: 100%;
  position: relative;
  border-radius: 20px;
  margin-bottom: 20px;
}

.slider-track {
  display: flex;
  transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;
  touch-action: pan-y pinch-zoom;
}

.slider-slide {
  flex: 0 0 100%;
  width: 100%;
  padding: 0 10px;
  box-sizing: border-box;
}

.slider-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin: 20px 0;
}

.nav-btn {
  background: var(--glass-bg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: var(--text-primary);
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.nav-btn:hover:not(:disabled) {
  background: var(--glass-bg);
  border-color: rgba(255, 255, 255, 0.4);
  transform: scale(1.05);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.slider-dots {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: var(--glass-bg);
  cursor: pointer;
  transition: all 0.3s ease;
}

.dot.active {
  background: var(--gradient-green-blue);
  width: 24px;
  border-radius: 12px;
}

.dot:hover:not(.active) {
  background: var(--accent-green, rgba(0, 255, 179, 0.5));
  transform: scale(1.2);
}

.slide-counter {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin-top: 10px;
  font-weight: 500;
}

.desktop-only {
  display: grid;
}

.mobile-only {
  display: none;
}

@media (max-width: 768px) {
  .trusted-by {
    padding: 60px 20px;
  }

  .trusted-by h2 {
    font-size: 2rem;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .slider-slide {
    padding: 0 5px;
  }

  .nav-btn {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }

  .slider-nav {
    gap: 15px;
  }
}

@media (max-width: 480px) {
  .nav-btn {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }

  .slider-nav {
    gap: 12px;
  }

  .dot {
    width: 6px;
    height: 6px;
  }

  .dot.active {
    width: 20px;
    border-radius: 10px;
  }
}
</style> 
<style>
.ig-embed-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
}
.ig-embed-backdrop {
  position: absolute;
  inset: 0;
  background: var(--overlay-medium);
}
.ig-embed-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 16px 40px 16px 16px;
  max-width: 560px;
  width: auto;
}

.ig-embed-content .instagram-media {
  background: var(--bg-primary) !important;
  border: none !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  margin: 0 !important;
  max-width: 100% !important;
  min-width: 100% !important;
  padding: 0 !important;
  width: 100% !important;
}

.ig-embed-content .instagram-media * {
  color: var(--text-primary) !important;
  background: var(--bg-primary) !important;
}

.ig-embed-content .instagram-media a {
  color: var(--text-primary) !important;
  background: transparent !important;
}

.ig-embed-content .instagram-media div {
  background: var(--bg-primary) !important;
}

.ig-embed-content .instagram-media iframe {
  background: var(--bg-primary) !important;
  border: none !important;
}

.instagram-loading {
  color: var(--text-primary);
  text-align: center;
  padding: 40px 20px;
  font-size: 1.1rem;
  background: var(--bg-primary);
  border-radius: 8px;
}
.ig-embed-close {
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 24px;
  cursor: pointer;
}
</style>
